'use client'

import { ChevronDownIcon } from '@heroicons/react/24/solid'
import cartIcon from '@public/images/cart.svg'
import Image from 'next/image'

import { Button } from '@src/components/Button/Button'
import { Container } from '@src/components/Container/Container'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { Stamp } from '@src/components/TopNavV3/Stamp'
import { useTopNav } from '@src/components/TopNavV3/TopNavContext'
import { configPublic } from '@src/config.public'
import { useCart } from '@src/hooks/useCart'
import { useLocale } from '@src/i18n/LocaleProvider'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { getCartQuantity } from '@src/utils/cart.util'
import { cn } from '@src/utils/tailwind'

import type { TopNavItem } from '../TopNavProps'

const itemClasses =
  'flex items-center gap-2 px-4 block cursor-pointer h-full transition-all hover:text-bodi-white'

const hoverTimeouts: Record<string, number> = {}
const hoverDelay = configPublic.general.topNavDelay

export const TopNavDesktopBar = () => {
  const { t } = useLocale('common')
  const { cart } = useCart()
  const quantity = getCartQuantity(cart)
  const trackSelectNavCta = useTrackSelectNavCta()
  const {
    logo,
    items,
    logInCta,
    signUpCta,
    isExpanded,
    expandedMenu,
    setExpandedMenu,
    setExpanded,
  } = useTopNav()

  const renderItems = (items?: TopNavItem[]) => {
    return items
      ?.filter((item) => Boolean(item.title))
      .map((item, index) => {
        const isSelected = item.title === expandedMenu?.title && isExpanded
        const componentKey = String(item.title)

        if (!item?.hasSubItems) {
          return (
            <LocalizedLink
              onMouseEnter={() => setExpanded(false)}
              onClick={() => {
                trackSelectNavCta({ 'cta url': item.href })
              }}
              key={componentKey}
              className={itemClasses}
              href={item.href ?? ''}
              rel={item.newTab ? 'noopener noreferrer' : ''}
              target={item.newTab ? '_blank' : '_self'}
            >
              {item.title}
              <Stamp text={item.stamp} />
            </LocalizedLink>
          )
        }

        return (
          <button
            data-testid={`nav-item-${index}`}
            key={componentKey}
            onMouseEnter={() => {
              setExpandedMenu(item)
              hoverTimeouts[componentKey] = window.setTimeout(() => setExpanded(true), hoverDelay)
            }}
            onMouseLeave={() => {
              clearTimeout(hoverTimeouts[componentKey])
            }}
            onClick={() => {
              clearTimeout(hoverTimeouts[componentKey])
              setExpanded(!isExpanded)
            }}
            className={cn(itemClasses, 'relative flex appearance-none items-center gap-2', {
              'text-bodi-white': isSelected,
            })}
          >
            {item.title}
            <Stamp text={item.stamp} />

            <ChevronDownIcon
              className={cn('size-5 transition-all', {
                'rotate-180': isSelected,
              })}
            />
          </button>
        )
      })
  }

  return (
    <div className="relative z-50 h-20 w-full bg-bodi-black-text font-medium text-bodi-gray-butters">
      <Container className="h-full *:h-full">
        <div className="flex h-full items-center">
          <div className="flex h-full items-center">
            {logo?.src && (
              <LocalizedLink
                onMouseEnter={() => setExpanded(false)}
                onClick={() => {
                  trackSelectNavCta({ 'cta url': logo?.href })
                }}
                href={logo?.href ?? ''}
                className="flex h-full items-center pr-4"
              >
                <Image
                  src={logo.src}
                  width={logo?.desktopWidth}
                  height={logo?.desktopHeight}
                  alt={logo?.altText ?? ''}
                />
              </LocalizedLink>
            )}
            {renderItems(items)}
          </div>
          <div
            onMouseEnter={() => setExpanded(false)}
            className="ml-auto flex h-full flex-1 items-center justify-end gap-8"
          >
            <LocalizedLink data-testid="cartBtn" href="/cart" className="relative mt-0.5">
              <Image src={cartIcon} width={24} height={24} alt={t('view-cart')} />
              {quantity ? (
                <div
                  className={cn(
                    'absolute top-0 left-full -mt-3 ml-[-18px] flex min-h-[24px] min-w-[24px] items-center justify-center rounded-full border-2 border-bodi-black-text bg-bodi-green-bright-500 text-body-xs font-semibold text-bodi-black',
                    { 'px-1.5': quantity > 99 }
                  )}
                >
                  {quantity > 999 ? '999' : quantity}
                </div>
              ) : null}
            </LocalizedLink>
            <LocalizedLink
              onClick={() => {
                trackSelectNavCta({ 'cta url': logInCta?.href })
              }}
              data-testid="logInBtn"
              href={logInCta?.href ?? ''}
              className="flex cursor-pointer gap-2 hover:text-bodi-white"
              rel={logInCta?.newTab ? 'noopener noreferrer' : ''}
              target={logInCta?.newTab ? '_blank' : '_self'}
            >
              {logInCta?.title}
            </LocalizedLink>
            <LocalizedLink
              onClick={() => {
                trackSelectNavCta({ 'cta url': signUpCta?.href })
              }}
              data-testid="signUpBtn"
              href={signUpCta?.href ?? ''}
              rel={signUpCta?.newTab ? 'noopener noreferrer' : ''}
              target={signUpCta?.newTab ? '_blank' : '_self'}
            >
              <Button className="[&>div]:px-8" size="small">
                {signUpCta?.title}
              </Button>
            </LocalizedLink>
          </div>
        </div>
      </Container>
    </div>
  )
}
